import { DIAGRAM_PITCH_HEIGHT, roundCoordinate } from './training.js';
/* ------------------------------------------------------------------ *
 * Bausteine zum Zeichnen: Koordinaten in x (Prozent der Breite) und y in Zeichnungseinheiten
 * ------------------------------------------------------------------ */
const AREA_HEIGHT = DIAGRAM_PITCH_HEIGHT.AREA;
const X = (value) => roundCoordinate(value);
/** y in Zeichnungseinheiten (Breite 100) als Prozent der Hoehe der freien Flaeche. */
const Y = (units) => roundCoordinate((units * 100) / AREA_HEIGHT);
const P = (x, y) => [X(x), Y(y)];
const cone = (x, y) => ({ type: 'cone', x: X(x), y: Y(y) });
const pole = (x, y) => ({ type: 'pole', x: X(x), y: Y(y) });
const ring = (x, y) => ({ type: 'ring', x: X(x), y: Y(y) });
const ball = (x, y) => ({ type: 'ball', x: X(x), y: Y(y) });
const hurdle = (x, y, rotation) => ({ type: 'hurdle', x: X(x), y: Y(y), rotation });
const player = (team, x, y, label) => ({ type: 'player', team, x: X(x), y: Y(y), label });
const goal = (x, y, size, rotation) => ({ type: 'goal', x: X(x), y: Y(y), size, rotation });
const text = (x, y, value) => ({ type: 'text', x: X(x), y: Y(y), text: value });
const zone = (x, y, w, h, extra = {}) => ({
    type: 'zone',
    x: X(x),
    y: Y(y),
    w: X(w),
    h: Y(h),
    ...extra,
});
/** Masslinie mit Platzhalter-Beschriftung: der Editor setzt die richtige beim Einfuegen aus der Laenge (siehe oben). */
const measure = (x1, y1, x2, y2) => ({ type: 'measure', from: P(x1, y1), to: P(x2, y2), label: '0 m' });
/** Pfeil von Punkt a nach Punkt b, an beiden Enden um `inset` Einheiten gekuerzt (er beginnt am Rand des Spielers, nicht in dessen Mitte). */
function link(kind, a, b, step, inset = 7) {
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const along = (t) => [a[0] + ((b[0] - a[0]) * t) / length, a[1] + ((b[1] - a[1]) * t) / length];
    return { type: 'arrow', kind, from: P(...along(inset)), to: P(...along(length - inset)), step };
}
/** Punkte auf einem Kreis um (cx, cy), im Uhrzeigersinn ab oben. */
function onCircle(cx, cy, radius, count) {
    return Array.from({ length: count }, (_, index) => {
        const angle = (index * 2 * Math.PI) / count;
        return [cx + radius * Math.sin(angle), cy - radius * Math.cos(angle)];
    });
}
/* ------------------------------------------------------------------ *
 * Die Aufbauten
 * ------------------------------------------------------------------ */
function stationCircle(count) {
    const stations = onCircle(50, 40, 25, count);
    const labels = onCircle(50, 40, 35, count);
    return [
        zone(22, 12, 56, 56, { shape: 'circle' }),
        ...stations.map(([x, y]) => cone(x, y)),
        ...labels.map(([x, y], index) => text(x, y, String(index + 1))),
    ];
}
const passTriangle = [[50, 20], [70, 54], [30, 54]];
const passSquare = [[30, 20], [70, 20], [70, 58], [30, 58]];
/** Slalom: sechs Stangen auf einer Linie; das Dribbling laeuft im Zickzack dazwischen durch. */
const slalomPoles = [25, 37, 49, 61, 73, 85];
export const DIAGRAM_SETUPS = [
    {
        key: 'rondo-quadrat',
        name: 'Rondo-Quadrat 10 × 10 m',
        description: '4 Hütchen, 4 Spieler A außen, 2 Spieler B in der Mitte, 1 Ball',
        pitch: 'AREA',
        elements: [
            cone(33, 20), cone(67, 20), cone(67, 54), cone(33, 54),
            player('A', 50, 20, '1'), player('A', 67, 37, '2'), player('A', 50, 54, '3'), player('A', 33, 37, '4'),
            player('B', 44.5, 40, '1'), player('B', 55.5, 34, '2'),
            ball(56, 22),
            measure(33, 61, 67, 61),
        ],
    },
    {
        key: 'feld-vier-minitore',
        name: 'Feld mit vier Minitoren',
        description: 'Rechteck mit einem Minitor je Seite, 3 gegen 3 und ein Ball',
        pitch: 'AREA',
        elements: [
            zone(20, 15, 60, 50),
            goal(50, 15, 'MINI', 0), goal(80, 40, 'MINI', 90), goal(50, 65, 'MINI', 180), goal(20, 40, 'MINI', 270),
            player('A', 36, 30, '1'), player('A', 42, 52, '2'), player('A', 57, 44, '3'),
            player('B', 64, 30, '1'), player('B', 58, 52, '2'), player('B', 43, 36, '3'),
            ball(50, 40),
        ],
    },
    {
        key: 'feld-zwei-tore-torhueter',
        name: 'Feld mit zwei Toren und Torhütern',
        description: 'Zwei Jugendtore mit je einem Torhüter, 3 gegen 3 und ein Ball',
        pitch: 'AREA',
        elements: [
            zone(15, 10, 70, 60),
            goal(50, 10, 'YOUTH', 0), goal(50, 70, 'YOUTH', 180),
            player('N', 50, 17, 'TW'), player('N', 50, 63, 'TW'),
            player('A', 32, 50, '1'), player('A', 50, 48, '2'), player('A', 68, 50, '3'),
            player('B', 32, 30, '1'), player('B', 50, 32, '2'), player('B', 68, 30, '3'),
            ball(57, 40),
        ],
    },
    {
        key: 'passdreieck',
        name: 'Passdreieck',
        description: '3 Spieler im Dreieck, Pässe im Kreis und ein Ball',
        pitch: 'AREA',
        elements: [
            ...passTriangle.map(([x, y], index) => player('A', x, y, String(index + 1))),
            ball(50, 27),
            ...passTriangle.map((from, index) => link('PASS', from, passTriangle[(index + 1) % passTriangle.length], index + 1)),
            measure(30, 62, 70, 62),
        ],
    },
    {
        key: 'passviereck',
        name: 'Passviereck',
        description: '4 Spieler im Viereck, Pässe im Kreis und ein Ball',
        pitch: 'AREA',
        elements: [
            ...passSquare.map(([x, y], index) => player('A', x, y, String(index + 1))),
            ball(30, 27),
            ...passSquare.map((from, index) => link('PASS', from, passSquare[(index + 1) % passSquare.length], index + 1)),
            measure(22, 20, 22, 58),
        ],
    },
    {
        key: 'stationskreis-4',
        name: 'Stationskreis mit 4 Stationen',
        description: 'Kreis mit 4 nummerierten Stationen (Hütchen)',
        pitch: 'AREA',
        elements: stationCircle(4),
    },
    {
        key: 'stationskreis-5',
        name: 'Stationskreis mit 5 Stationen',
        description: 'Kreis mit 5 nummerierten Stationen (Hütchen)',
        pitch: 'AREA',
        elements: stationCircle(5),
    },
    {
        key: 'stationskreis-6',
        name: 'Stationskreis mit 6 Stationen',
        description: 'Kreis mit 6 nummerierten Stationen (Hütchen)',
        pitch: 'AREA',
        elements: stationCircle(6),
    },
    {
        key: 'slalom',
        name: 'Slalom (Stangenreihe)',
        description: '6 Stangen auf einer Linie, ein Spieler dribbelt im Zickzack durch',
        pitch: 'AREA',
        elements: [
            ...slalomPoles.map((x) => pole(x, 40)),
            player('A', 8, 40, '1'),
            ball(14, 44),
            {
                type: 'arrow',
                kind: 'DRIBBLE',
                from: P(15, 40),
                to: P(94, 40),
                step: 1,
                points: [P(31, 31), P(43, 49), P(55, 31), P(67, 49), P(79, 31)],
            },
            measure(25, 56, 37, 56),
        ],
    },
    {
        key: 'koordinationsleiter',
        name: 'Koordinationsleiter (Ringreihe)',
        description: '6 Ringe in einer Reihe, ein Spieler läuft darüber',
        pitch: 'AREA',
        elements: [
            ...[17.5, 30.5, 43.5, 56.5, 69.5, 82.5].map((x) => ring(x, 40)),
            player('A', 7, 40, '1'),
            link('RUN', [7, 40], [95, 40], 1, 6),
            measure(17.5, 52, 30.5, 52),
        ],
    },
    {
        key: 'huerdenreihe',
        name: 'Hürdenreihe',
        description: '5 Hürden quer zur Laufrichtung, ein Spieler läuft darüber',
        pitch: 'AREA',
        elements: [
            ...[25, 40, 55, 70, 85].map((x) => hurdle(x, 40, 90)),
            player('A', 8, 40, '1'),
            link('RUN', [8, 40], [96, 40], 1, 6),
            measure(25, 52, 40, 52),
        ],
    },
    {
        key: 'zonenspiel-drei-zonen',
        name: 'Zonenspiel mit drei Zonen',
        description: 'Drei Zonen nebeneinander (Gelb, Weiß, Blau) mit zwei Teams und einem Joker',
        pitch: 'AREA',
        elements: [
            zone(5, 15, 30, 50, { color: 'yellow' }),
            zone(35, 15, 30, 50),
            zone(65, 15, 30, 50, { color: 'blue' }),
            player('A', 14, 28, '1'), player('A', 25, 40, '2'), player('A', 14, 52, '3'),
            player('B', 45, 30, '1'), player('B', 56, 42, '2'), player('B', 45, 54, '3'),
            player('N', 84, 40, 'J'),
            ball(28, 44),
        ],
    },
    {
        key: 'eins-gegen-eins-kanal',
        name: '1-gegen-1-Kanal',
        description: 'Schmaler Kanal mit einem Minitor an jedem Ende, Angreifer gegen Verteidiger',
        pitch: 'AREA',
        elements: [
            zone(38, 12, 24, 56),
            goal(50, 12, 'MINI', 0), goal(50, 68, 'MINI', 180),
            player('A', 50, 56, '1'),
            player('B', 50, 33, '1'),
            ball(50, 49),
            measure(38, 74, 62, 74),
        ],
    },
    {
        key: 'torschuss-aufbau',
        name: 'Torschuss-Aufbau',
        description: 'Tor mit Torhüter, Hütchenreihe als Startlinie, ein Schütze mit Ball',
        pitch: 'AREA',
        elements: [
            goal(50, 10, 'YOUTH', 0),
            player('N', 50, 17, 'TW'),
            cone(25, 46), cone(42, 46), cone(58, 46), cone(75, 46),
            player('A', 50, 62, '1'),
            ball(50, 56),
            { type: 'arrow', kind: 'SHOT', from: P(50, 54), to: P(38, 12), step: 1 },
        ],
    },
];
export const setupByKey = new Map(DIAGRAM_SETUPS.map((setup) => [setup.key, setup]));
//# sourceMappingURL=training-setups.js.map